import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {MaterialNode} from "#template/nodes/material/material-node"
import type {OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {ImageLikeNode, MaterialLikeNode, NumberLike} from "#template/nodes/types"
import {MaterialSlotType} from "#template/nodes/types"
import {imageLike} from "#template/nodes/image/fundamental"
import {materialLike} from "#template/nodes/material/fundamental"
import {numberLike} from "#template/nodes/number/fundamental"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {MaterialGraph} from "@cm/material-nodes"
import {TransformMaterialSlot} from "#template/runtime-graph/nodes/transform-material-slot"
import {visitNone} from "#template/template-node/visit-helpers"

export type BlendMaterialSlotType = MaterialSlotType
export const blendMaterialSlotType = z.enum(MaterialSlotType)

export type BlendMaterialSlotParameters = {
    name: string
    material: MaterialLikeNode | null
    image: ImageLikeNode | null
    slot: BlendMaterialSlotType
    blendAmount: NumberLike
    uvChannel: number
    size: [NumberLike, NumberLike]
}

const blendMaterialSlotDefinition: TemplateNodeDefinition<BlendMaterialSlotParameters> = {
    nodeClass: "BlendMaterialSlot",
    validation: {
        parameters: z.object({
            name: z.string(),
            material: z.nullable(materialLike),
            image: z.nullable(imageLike),
            slot: blendMaterialSlotType,
            blendAmount: numberLike,
            uvChannel: z.number(),
            size: z.tuple([numberLike, numberLike]),
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class BlendMaterialSlot extends MaterialNode<BlendMaterialSlotParameters> {
    public override get definition() {
        return blendMaterialSlotDefinition
    }

    public override onFilterActive(
        _context: OnFilterActiveContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<BlendMaterialSlotParameters> {
        if (this.parameters.material === null || this.parameters.image === null || this.parameters.blendAmount === null) return skipped
        return visitNone(this.parameters)
    }

    public override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<MaterialGraph | null> {
        const material = evaluator.evaluate(this.parameters.material)
        const [materialValid, materialInvalid] = scope.branch(material)

        const image = evaluator.evaluate(this.parameters.image)

        const [amountValid, amountInvalid] = scope.branch(evaluator.evaluate(this.parameters.blendAmount))

        const [sizeXValid, sizeXInvalid] = scope.branch(evaluator.evaluate(this.parameters.size[0]))
        const [sizeYValid, sizeYInvalid] = scope.branch(evaluator.evaluate(this.parameters.size[1]))

        const result = scope.node(TransformMaterialSlot, {
            sceneManager: evaluator.templateContext.sceneManager,
            material: materialValid,
            image,
            size: scope.tuple(sizeXValid, sizeYValid),
            slot: this.parameters.slot,
            blendAmount: amountValid,
            uvChannel: this.parameters.uvChannel,
        }).outputMaterial
        return scope.phi(result, materialInvalid, sizeXInvalid, sizeYInvalid, amountInvalid)
    }
}
